import { useState, useEffect } from 'react'
import { getBuildInfo } from '../services/buildService'
import type { BuildInfo } from '../types/build.types'

interface UseBuildInfoReturn {
  buildInfo: BuildInfo | null
  isLoading: boolean
  error: string | null
}

/**
 * Hook to fetch and manage build version information
 * Automatically fetches build info on mount
 * 
 * @returns Build info, loading state, and error
 */
export function useBuildInfo(): UseBuildInfoReturn {
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchBuildInfo = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const data = await getBuildInfo()
        setBuildInfo(data)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch build info'
        setError(errorMessage)
        console.error('Error fetching build info:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchBuildInfo()
  }, [])

  return {
    buildInfo,
    isLoading,
    error,
  }
}
